// LLM:METADATA
// :topology: [Domain:StateManagement | Role:AuthState | Pattern:Module]
// :relates-to: uses: "api.js", required-by: "useAuth.js", "Login.vue"
// :rationale: "Centralized authentication state management"
// :contract: pre: "Auth action dispatched", post: "Auth state updated", invariant: "User role is always a string"
// :decision_cache: "Chose Vuex module for centralized auth state [AUTH-001]"
// LLM:END

import { USER_ROLE_PERMISSIONS } from "@/constants/permissions"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_STORE_AUTH } from "@/constants/topology"
import logger from "@/services/logger"
import {
  STATE_MUTATION_START,
  STATE_MUTATION_COMPLETE,
  STATE_ACTION_START,
  STATE_ACTION_COMPLETE,
} from "@/constants/events"

// Set topology ID for semantic logging
setTopologyId(TOPOLOGY_STORE_AUTH)

export default {
  namespaced: false,
  state: {
    // CRITICAL FIX: Initialize from localStorage to prevent empty state on page load
    // Tests inject localStorage.token/userRole/userId BEFORE Vue app mounts
    // If Vuex store doesn't read localStorage, isAuthenticated === false → no content renders
    userRole: localStorage.getItem("userRole") || "",
    userId: parseInt(localStorage.getItem("userId")) || 0,
    // Legacy fallback only. Active UI authority now comes from backend `accessProfile`.
    userRolePermissions: USER_ROLE_PERMISSIONS,
    accessProfile: null,
    permissionsVersion: 0,
    effectiveRole: null,
    // LLM:METADATA
    // :topology: [Domain:Authentication | Role:AuthStateMachine | Pattern:StateTracking]
    // :relates-to: uses: "useAuth.js", required-by: "router/index.js"
    // :rationale: "Tracks authentication status to prevent protected route fan-out before auth validation"
    // :contract: pre: "Auth state initialized", post: "Auth status transitions: unknown -> checking -> authenticated|unauthenticated", invariant: "Status is one of 4 valid values"
    // :decision_cache: "Chose 4-state machine for clear auth lifecycle tracking [AUTH-STATE-001]"
    // LLM:END
    authStatus: "unknown", // Values: unknown | checking | authenticated | unauthenticated
  },
  getters: {
    getUserRole: (state) => () => state.userRole,
    getUserId: (state) => () => state.userId,
    getUserRolePermissions: (state) => () => {
      if (state.userRole != "") return state.userRolePermissions[state.userRole]
      else return {}
    },
    getAccessProfile: (state) => () => state.accessProfile,
    getPermissionsVersion: (state) => () => state.permissionsVersion,
    getEffectiveRole: (state) => () => state.effectiveRole,
    // LLM:METADATA
    // :topology: [Domain:Authentication | Role:AuthStatusGetter | Pattern:StateAccess]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "router/index.js"
    // :rationale: "Provides access to auth status state for router guard"
    // :contract: pre: "State initialized", post: "Returns auth status", invariant: "Returns one of 4 valid values"
    // :decision_cache: "Chose getter for reactive status access [AUTH-STATUS-003]"
    // LLM:END
    getAuthStatus: (state) => () => state.authStatus,
  },
  mutations: {
    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "App.vue"
    // :rationale: "Updates user role in Vuex store with logging"
    // :contract: pre: "New role value provided", post: "User role state updated", invariant: "Role is always a string"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-AUTH-001]"
    // LLM:END
    SET_USER_ROLE(state, p) {
      logger.debug(STATE_MUTATION_START, "Setting user role", {
        mutation: "SET_USER_ROLE",
        oldValue: state.userRole,
        newValue: p,
      })
      state.userRole = p
      logger.debug(STATE_MUTATION_COMPLETE, "User role set", {
        mutation: "SET_USER_ROLE",
        value: p,
      })
    },

    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "App.vue"
    // :rationale: "Updates user ID in Vuex store with logging"
    // :contract: pre: "New user ID value provided", post: "User ID state updated", invariant: "User ID is always a number"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-AUTH-002]"
    // LLM:END
    SET_USER_ID(state, p) {
      logger.debug(STATE_MUTATION_START, "Setting user ID", {
        mutation: "SET_USER_ID",
        oldValue: state.userId,
        newValue: p,
      })
      state.userId = p
      logger.debug(STATE_MUTATION_COMPLETE, "User ID set", {
        mutation: "SET_USER_ID",
        value: p,
      })
    },

    SET_ACCESS_PROFILE(state, profile) {
      state.accessProfile = profile || null
    },

    SET_PERMISSIONS_VERSION(state, version) {
      state.permissionsVersion = Number.isFinite(Number(version))
        ? Number(version)
        : 0
    },

    /**
     * Store the backend-declared effective role projection for access-related UI seams.
     *
     * @precondition effectiveRole is either a non-empty backend role code or nullish when unavailable.
     * @postcondition state.effectiveRole mirrors the current backend authority string or null.
     * @mutates state.effectiveRole
     */
    SET_EFFECTIVE_ROLE(state, effectiveRole) {
      const normalizedRole = String(effectiveRole || "").trim()
      state.effectiveRole = normalizedRole === "" ? null : normalizedRole
    },

    // LLM:METADATA
    // :topology: [Domain:Authentication | Role:AuthStatusMutation | Pattern:StateMachine]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "router/index.js"
    // :rationale: "Updates authentication status in state machine with logging"
    // :contract: pre: "Valid status value provided", post: "Auth status state updated", invariant: "Status is one of: unknown/checking/authenticated/unauthenticated"
    // :decision_cache: "Chose explicit status tracking for router guard synchronization [AUTH-STATUS-001]"
    // LLM:END
    SET_AUTH_STATUS(state, status) {
      const validStatuses = [
        "unknown",
        "checking",
        "authenticated",
        "unauthenticated",
      ]
      if (!validStatuses.includes(status)) {
        logger.error("SET_AUTH_STATUS_INVALID", "Invalid auth status", {
          mutation: "SET_AUTH_STATUS",
          provided: status,
          valid: validStatuses,
        })
        return
      }
      logger.debug(STATE_MUTATION_START, "Setting auth status", {
        mutation: "SET_AUTH_STATUS",
        oldValue: state.authStatus,
        newValue: status,
      })
      state.authStatus = status
      logger.debug(STATE_MUTATION_COMPLETE, "Auth status set", {
        mutation: "SET_AUTH_STATUS",
        value: status,
      })
    },
  },

  actions: {
    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateAction | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "App.vue"
    // :rationale: "Commits user role mutation to Vuex store"
    // :contract: pre: "New role value provided", post: "Mutation committed", invariant: "Action always commits a mutation"
    // :decision_cache: "Chose Vuex actions for encapsulated state changes [STATE-AUTH-003]"
    // LLM:END
    setUserRole({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting user role action", {
        action: "setUserRole",
        newValue,
      })
      commit("SET_USER_ROLE", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "User role action completed", {
        action: "setUserRole",
        value: newValue,
      })
    },

    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateAction | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "App.vue"
    // :rationale: "Commits user ID mutation to Vuex store"
    // :contract: pre: "New user ID value provided", post: "Mutation committed", invariant: "Action always commits a mutation"
    // :decision_cache: "Chose Vuex actions for encapsulated state changes [STATE-AUTH-004]"
    // LLM:END
    setUserId({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting user ID action", {
        action: "setUserId",
        newValue,
      })
      commit("SET_USER_ID", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "User ID action completed", {
        action: "setUserId",
        value: newValue,
      })
    },

    setAccessProfile({ commit }, profile) {
      commit("SET_ACCESS_PROFILE", profile)
    },

    setPermissionsVersion({ commit }, version) {
      commit("SET_PERMISSIONS_VERSION", version)
    },

    setEffectiveRole({ commit }, effectiveRole) {
      commit("SET_EFFECTIVE_ROLE", effectiveRole)
    },

    // LLM:METADATA
    // :topology: [Domain:Authentication | Role:AuthStatusAction | Pattern:StateMachine]
    // :relates-to: uses: "logger.js", required-by: "useAuth.js", "router/index.js"
    // :rationale: "Commits auth status mutation to update state machine"
    // :contract: pre: "Valid status value provided", post: "Mutation committed", invariant: "Action always commits a mutation"
    // :decision_cache: "Chose action wrapper for logging and future extensibility [AUTH-STATUS-002]"
    // LLM:END
    setAuthStatus({ commit }, status) {
      logger.debug(STATE_ACTION_START, "Setting auth status action", {
        action: "setAuthStatus",
        status,
      })
      commit("SET_AUTH_STATUS", status)
      logger.debug(STATE_ACTION_COMPLETE, "Auth status action completed", {
        action: "setAuthStatus",
        value: status,
      })
    },
  },
}
